import React from 'react';
import { Outlet, createBrowserRouter, useLocation } from 'react-router-dom';

import AdminPage, { child_routes } from './adminContent/AdminPage';
import AccountPage, { account_child_routes } from '@/content/AccountPage/AccountPage';
import { ProtectedAdminRoute } from './lib/ProtectedRoute';

import { Content } from '@carbon/react';
import DefaultHeader from 'components/DefaultHeader';
import RootModal from 'components/Modals/RootModal';
import RootSideForms from 'components/SideForms/RootSideForms';
import Footer from 'components/Footer';
import ToastStack from '@/components/Toasts/ToastStack';
import { ErrorPage } from 'content/ErrorPage';
import { StreamServiceProvider } from './contexts/StreamServiceContext';
import { AdminPageSideNav } from './adminContent/AdminPage';
import ProtectedRoute from './lib/ProtectedRoute';
import { AccountPageSideNav } from './content/AccountPage/AccountPage';

const LandingPage = React.lazy(() =>
  import(/* webpackChunkName: 'LandingPage' */ '@/content/LandingPage')
);
const ElementsPage = React.lazy(() =>
  import(/* webpackChunkName: 'ElementsPage' */ '@/content/ElementsPage')
);
const LocationsPage = React.lazy(() =>
  import(/* webpackChunkName: 'LocationsPage' */ '@/content/LocationsPage')
);
const DevicesPage = React.lazy(() =>
  import(/* webpackChunkName: 'DevicesPage' */ '@/content/DevicesPage/DevicesPage')
);
const StreamPage = React.lazy(() =>
  import(/* webpackChunkName: 'StreamPage' */ '@/content/StreamsPage')
);
const StreamsIndex = React.lazy(() =>
  import(/* webpackChunkName: 'StreamsIndex' */ '@/content/StreamsPage/StreamsIndex')
);
const HelpsPage = React.lazy(() =>
  import(/* webpackChunkName: 'HelpsPage' */ '@/content/HelpsPage')
);
const NewsListPage = React.lazy(() =>
  import(/* webpackChunkName: 'NewsListPage' */ '@/content/NewsPage/NewsListPage')
);
const NewsDetailPage = React.lazy(() =>
  import(/* webpackChunkName: 'NewsDetailPage' */ '@/content/NewsPage/NewsDetailPage')
);
const LoginPage = React.lazy(() =>
  import(/* webpackChunkName: 'LoginPage' */ '@/pages/loginPage')
);
const PasswordRecoveryPage = React.lazy(() =>
  import(/* webpackChunkName: 'PasswordRecoveryPage' */ '@/content/LoginPage/PasswordRecoveryPage.js')
);

export const LoggedInLayout = () => {
  const location = useLocation();
  return (
    <StreamServiceProvider>
      <DefaultHeader />
      {location.pathname.startsWith("/admin") && <AdminPageSideNav />}
      {location.pathname.startsWith("/me") && <AccountPageSideNav />}
      <Content>
        <Outlet />
      </Content>
      <RootSideForms />
      <RootModal />
      <ToastStack />
      {(location.pathname.startsWith("/admin") || location.pathname.startsWith("/me")) || <Footer />}
    </StreamServiceProvider>
  );
}

export const routes = [
  {
    path : '/',
    element: <ProtectedRoute><LoggedInLayout /></ProtectedRoute>,
    children: [
      {
        index: true,
        element: <LandingPage/>,
      },
      {
        path: 'elements',
        element: <ElementsPage/>,
      },
      {
        path: 'locations',
        element: <LocationsPage/>,
      },
      {
        path: 'devices',
        element: <ProtectedAdminRoute><DevicesPage/></ProtectedAdminRoute>,
      },
      {
        path: 'location/:location_id',
        element: <LocationsPage/>,
      },
      {
        path: 'device/:device_id',
        element: <LocationsPage/>,
      },
      {
        path: 'streams',
        element: <StreamsIndex/>,
      },
      {
        path: 'help',
        element: <HelpsPage/>,
      },
      {
        path: 'news',
        element: <NewsListPage/>,
      },
      {
        path: 'news/:id',
        element: <NewsDetailPage/>,
      },
      {
        path: 'stream/:id',
        element: <StreamPage/>,
      },
      {
        path: '404',
        element: <ErrorPage/>,
      },
      {
        path: 'admin',
        element: <ProtectedAdminRoute><AdminPage/></ProtectedAdminRoute>,
        children: child_routes,
      },
      {
        path: 'me',
        element: <AccountPage/>,
        children: account_child_routes
      }
    ]
  },
  {
    path: '/logout',
    element: <LoginPage/>,
  },
  {
    // The recovery link from the mail may be opened in a browser that still
    // holds a valid session. Without this route the logged-in router had no
    // match at all and the user was left on an error page.
    path: '/password_recovery/:token',
    element: <PasswordRecoveryPage/>,
  },
];
export default createBrowserRouter(routes);
